import type {
  PowerSyncCollectionRow,
  PowerSyncDeterminationRow,
} from "@/lib/powersync/schema"
import type { CollectionWithDetermination } from "@fieldbook/common/types"
import { useQuery } from "@powersync/tanstack-react-query"
import { useSpecies } from "./useSpecies"
import { useSyncFailureForEntity } from "./useSyncFailures"

/**
 * The subset of collection fields that can appear in sync failure op_data.
 * Determination fields come from a separate query and won't be in the CRUD payload.
 */
type CollectionOpData = {
  project_id?: string
  collection_number?: string
  field_name?: string
  nano_id?: string
  notes?: string
  created_by?: string
  created_at?: string
  updated_at?: string
}

function opDataToCollection(
  id: string,
  opData: CollectionOpData,
  base?: CollectionWithDetermination,
): CollectionWithDetermination {
  return {
    id,
    project_id: opData.project_id ?? base?.project_id ?? "",
    collection_number:
      opData.collection_number ?? base?.collection_number ?? "",
    field_name: opData.field_name ?? base?.field_name ?? "",
    nano_id: opData.nano_id ?? base?.nano_id ?? "",
    notes: opData.notes ?? base?.notes ?? null,
    created_by: opData.created_by ?? base?.created_by ?? "",
    created_at: opData.created_at ?? base?.created_at ?? "",
    updated_at: opData.updated_at ?? base?.updated_at ?? "",
    // Determination fields only come from the DB queries, not from op_data
    determination_id: base?.determination_id,
    determined_species_id: base?.determined_species_id,
    determined_species_name: base?.determined_species_name,
    determined_species_ala_guid: base?.determined_species_ala_guid,
    determined_species_metadata: base?.determined_species_metadata,
    determined_by: base?.determined_by,
    determined_at: base?.determined_at,
    determination_notes: base?.determination_notes,
    determined_species_indeterminate: base?.determined_species_indeterminate,
  }
}

export const getCollectionQueryOptions = (collectionId?: string) => ({
  queryKey: ["collections", "detail", collectionId],
})

export type CollectionWithSyncStatus = CollectionWithDetermination & {
  syncFailed?: boolean
}

type CollectionRow = PowerSyncCollectionRow & { id: string }
type DeterminationRow = PowerSyncDeterminationRow & { id: string }

/**
 * Hook to get a single collection by ID, with its latest determination and species.
 *
 * Uses three separate PowerSync reactive queries (collections, determinations, species)
 * so each table change triggers a reliable reactive update. A single JOIN with subquery
 * was unreliable — PowerSync did not consistently detect determinations/species changes.
 */
export const useCollection = ({
  collectionId,
}: {
  collectionId?: string
  projectId?: string
}): {
  data: CollectionWithSyncStatus | null | undefined
  isPending: boolean
  isError: boolean
  error: Error | null
} => {
  // Query 1: collection row — PowerSync watches `collections`
  const collectionQuery = useQuery<CollectionRow>({
    ...getCollectionQueryOptions(collectionId),
    query: "SELECT * FROM collections WHERE id = ? LIMIT 1",
    parameters: [collectionId ?? ""],
    enabled: Boolean(collectionId),
  })

  // Query 2: latest determination — PowerSync watches `determinations`
  const determinationQuery = useQuery<DeterminationRow>({
    queryKey: [
      "determinations",
      "latest-for-collection",
      collectionId,
      collectionQuery.dataUpdatedAt,
    ],
    query:
      "SELECT * FROM determinations WHERE collection_id = ? ORDER BY determined_at DESC, created_at DESC LIMIT 1",
    parameters: [collectionId ?? ""],
    enabled: Boolean(collectionId),
  })

  const determinationRow = determinationQuery.data?.[0]

  // Query 3: species for the latest determination — PowerSync watches `species`
  const { data: speciesData } = useSpecies(
    determinationRow?.species_id ?? undefined,
  )

  const failure = useSyncFailureForEntity<CollectionOpData>(
    "collections",
    collectionId,
  )

  const collectionRow = collectionQuery.data?.[0]

  let data: CollectionWithSyncStatus | null | undefined

  if (collectionRow) {
    const base: CollectionWithDetermination = {
      id: collectionRow.id,
      project_id: collectionRow.project_id ?? "",
      collection_number: collectionRow.collection_number ?? "",
      field_name: collectionRow.field_name ?? "",
      nano_id: collectionRow.nano_id ?? "",
      notes: collectionRow.notes ?? null,
      created_by: collectionRow.created_by ?? "",
      created_at: collectionRow.created_at ?? "",
      updated_at: collectionRow.updated_at ?? "",
      determination_id: determinationRow?.id ?? undefined,
      determined_species_id: determinationRow?.species_id ?? undefined,
      determined_by: determinationRow?.determined_by ?? undefined,
      determined_at: determinationRow?.determined_at ?? undefined,
      determination_notes: determinationRow?.notes ?? undefined,
      determined_species_indeterminate: Boolean(
        determinationRow?.species_indeterminate,
      ),
      determined_species_name: speciesData?.name ?? undefined,
      determined_species_ala_guid: speciesData?.ala_guid ?? undefined,
      determined_species_metadata: speciesData?.metadata ?? undefined,
    }

    if (failure) {
      data = {
        ...opDataToCollection(collectionRow.id, failure.parsedOpData, base),
        syncFailed: true,
      }
    } else {
      data = base
    }
  } else if (failure) {
    data = {
      ...opDataToCollection(failure.entity_id, failure.parsedOpData),
      syncFailed: true,
    }
  } else {
    data = collectionQuery.data ? null : undefined
  }

  return {
    data,
    isPending: collectionQuery.isPending,
    isError: collectionQuery.isError,
    error: collectionQuery.error,
  }
}
